import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import type { ReactNode } from 'react';
import { twJoin } from 'tailwind-merge';

import { FadedHorizontalScroll } from '@/components/chat/FadedHorizontalScroll';
import { HStack } from '@/components/ui/hstack';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';

export type GroupedChatTabs = {
  routes: { conversationId: string; title: string }[];
  activeConversationId: string;
  onSelectConversation: (conversationId: string) => void;
  description?: string;
};

type MobileChatGroupedTabsSectionProps = {
  groupedTabs?: GroupedChatTabs;
  isSmallDevice: boolean;
  activeTabTextColor: string;
  inactiveTabTextColor: string;
  /** Rendered below the tabs section on mobile. Used for linked inquiry banner. */
  bannerSlot?: ReactNode;
};

export function MobileChatGroupedTabsSection({
  groupedTabs,
  isSmallDevice,
  activeTabTextColor,
  inactiveTabTextColor,
  bannerSlot,
}: MobileChatGroupedTabsSectionProps) {
  const shouldRenderGroupedMobileTabs = groupedTabs != null && groupedTabs.routes.length > 1;

  /** Narrow web: avoid horizontal tab strip; use stacked rows (matches desktop inbox on small web). */
  const useStackedGroupedConversationPicker = isWeb && isSmallDevice;

  if (shouldRenderGroupedMobileTabs && groupedTabs && useStackedGroupedConversationPicker) {
    const groupedTabsDescription = groupedTabs.description?.trim();

    const descriptionNode = groupedTabsDescription ? (
      <Text size="sm" className="px-4 pt-3 font-medium text-secondary-700">
        {groupedTabsDescription}
      </Text>
    ) : null;

    return (
      <VStack className="border-b border-outline-100 bg-background-0 shadow-sm max-md:border-b-0">
        {descriptionNode}
        <VStack className="gap-2 px-4 py-3">
          {groupedTabs.routes.map((route) => {
            const isCurrent = route.conversationId === groupedTabs.activeConversationId;

            return (
              <Pressable
                key={route.conversationId}
                onPress={() => groupedTabs.onSelectConversation(route.conversationId)}
                {...getAccessibilityProps({ label: route.title, role: 'button' })}
                accessibilityState={{ selected: isCurrent }}
                className={twJoin(
                  'rounded-lg border px-3 py-2.5',
                  isCurrent
                    ? 'border-outline-200 bg-background-50'
                    : 'border-transparent bg-background-0'
                )}>
                <Text
                  className={twJoin(
                    'text-left text-sm',
                    isCurrent
                      ? 'font-semibold text-typography-900'
                      : 'font-medium text-typography-700'
                  )}
                  numberOfLines={3}>
                  {route.title}
                </Text>
              </Pressable>
            );
          })}
        </VStack>
        {bannerSlot}
      </VStack>
    );
  }

  if (shouldRenderGroupedMobileTabs && groupedTabs) {
    const shouldFitAllTabsOnCompactScreen = isSmallDevice && groupedTabs.routes.length <= 3;

    const shouldCenterTabs = isWeb || groupedTabs.routes.length <= 3;

    const getTabListClassName = () => {
      if (shouldFitAllTabsOnCompactScreen) {
        return 'w-full gap-0';
      }

      if (shouldCenterTabs) {
        return 'mx-auto gap-3';
      }

      return 'justify-start -ml-1 gap-3';
    };

    const getTabTitle = (route: GroupedChatTabs['routes'][number]) => route.title;

    const getTabStyle = (): { flex: number } | undefined => {
      if (shouldFitAllTabsOnCompactScreen) {
        return { flex: 1 };
      }

      return undefined;
    };

    const getTabClassName = (isCurrent: boolean) => {
      if (shouldFitAllTabsOnCompactScreen) {
        return twJoin(
          'w-0 min-w-0 overflow-hidden px-1',
          'items-center border-b-2 px-2 py-3',
          isCurrent ? 'border-b-primary-500' : 'border-b-transparent'
        );
      }

      if (isWeb) {
        return twJoin(
          'w-[180px] max-w-[180px] overflow-hidden',
          'items-center border-b-2 px-2 py-3',
          isCurrent ? 'border-b-primary-500' : 'border-b-transparent'
        );
      }

      return twJoin(
        'w-[100px]',
        'items-center border-b-2 px-2 py-3',
        isCurrent ? 'border-b-primary-500' : 'border-b-transparent'
      );
    };

    const getTabTextClassName = () => {
      if (isWeb || shouldFitAllTabsOnCompactScreen) {
        return 'block w-full text-center';
      }

      return 'text-center';
    };

    const tabTextSize = shouldFitAllTabsOnCompactScreen && isWeb ? 'xs' : 'sm';

    const tabList = (
      <HStack className={getTabListClassName()}>
        {groupedTabs.routes.map((route) => {
          const isCurrent = route.conversationId === groupedTabs.activeConversationId;

          return (
            <Pressable
              key={route.conversationId}
              style={getTabStyle()}
              className={getTabClassName(isCurrent)}
              onPress={() => groupedTabs.onSelectConversation(route.conversationId)}
              {...getAccessibilityProps({ label: route.title, role: 'button' })}
              accessibilityState={{ selected: isCurrent }}>
              <Text
                size={tabTextSize}
                className={getTabTextClassName()}
                numberOfLines={1}
                isTruncated
                style={{
                  color: isCurrent ? activeTabTextColor : inactiveTabTextColor,
                }}>
                {getTabTitle(route)}
              </Text>
            </Pressable>
          );
        })}
      </HStack>
    );

    const groupedTabsDescription = groupedTabs.description?.trim();

    const descriptionNode = groupedTabsDescription ? (
      <Text size="sm" className="px-5 pt-3 font-medium text-secondary-700">
        {groupedTabsDescription}
      </Text>
    ) : null;

    if (shouldFitAllTabsOnCompactScreen) {
      return (
        <VStack className="border-b border-outline-100 bg-background-50 shadow-sm max-md:border-b-0">
          {descriptionNode}
          {tabList}
          {bannerSlot}
        </VStack>
      );
    }

    return (
      <VStack className="border-b border-outline-100 bg-background-50 shadow-sm max-md:border-b-0">
        {descriptionNode}
        <FadedHorizontalScroll
          minContentWidth={shouldCenterTabs ? '100%' : groupedTabs.routes.length * 100}>
          {tabList}
        </FadedHorizontalScroll>
        {bannerSlot}
      </VStack>
    );
  }

  if (bannerSlot) {
    return <>{bannerSlot}</>;
  }

  return null;
}
